// src/admin/api/orders.ts
import {
  ListQuery,
  OrderDetail,
  OrderImageMaintenanceResponse,
  OrderListResponse,
  OrderStatus,
  PaymentStatus,
  FulfillmentStatus,
} from "../types/order";
import { http } from "./http";

// -------- Normalize incoming values to exact backend enums --------
function normalizeOrderStatus(v?: string): OrderStatus | undefined {
  if (!v) return undefined;
  const val = v.trim().toLowerCase();
  return Object.values(OrderStatus).includes(val as OrderStatus)
    ? (val as OrderStatus)
    : undefined;
}

function normalizePaymentStatus(v?: string): PaymentStatus | undefined {
  if (!v) return undefined;
  const val = v.trim().toLowerCase();
  return Object.values(PaymentStatus).includes(val as PaymentStatus)
    ? (val as PaymentStatus)
    : undefined;
}

function normalizeFulfillmentStatus(v?: string): FulfillmentStatus | undefined {
  if (!v) return undefined;
  const val = v.trim().toLowerCase();
  return Object.values(FulfillmentStatus).includes(val as FulfillmentStatus)
    ? (val as FulfillmentStatus)
    : undefined;
}

const isObjectId = (s: string) => /^[0-9a-f]{24}$/i.test(s);
const isOrderNumber = (s: string) => /^ORD-\d{4}-[A-Z0-9]{5}$/i.test(s);

export const OrdersAPI = {
  async list(params: ListQuery) {
    const r = await http.get<OrderListResponse>("/orders", { params });
    return r.data;
  },

  // 🔧 FIX: route order numbers to /by-number and ObjectIds to /by-id
  async get(idOrNumber: string) {
    if (isOrderNumber(idOrNumber)) {
      const r = await http.get<OrderDetail>(
        `/orders/by-number/${encodeURIComponent(idOrNumber)}`
      );
      return r.data;
    }
    if (isObjectId(idOrNumber)) {
      // Only works if you added GET /orders/by-id/:id on the BE (recommended)
      const r = await http.get<OrderDetail>(
        `/orders/by-id/${encodeURIComponent(idOrNumber)}`
      );
      return r.data;
    }
    // Fallback: try by-number (keeps old links working)
    const r = await http.get<OrderDetail>(
      `/orders/by-number/${encodeURIComponent(idOrNumber)}`
    );
    return r.data;
  },

  async updateStatuses(
    id: string,
    payload: {
      status?: string;
      paymentStatus?: string;
      fulfillmentStatus?: string;
      refund?: {
        amount?: number;
        reason?: string;
        note?: string;
        source?: string;
        processor?: string;
        processorRefundId?: string;
        refundedAt?: string;
        fullRefund?: boolean;
        items?: Array<{
          productId?: string;
          name?: string;
          quantity?: number;
          amount?: number;
          reason?: string;
        }>;
      };
    }
  ) {
    const body: Partial<{
      status: OrderStatus;
      paymentStatus: PaymentStatus;
      fulfillmentStatus: FulfillmentStatus;
      refund: typeof payload.refund;
    }> = {};

    const s = normalizeOrderStatus(payload.status);
    if (s) body.status = s;

    const p = normalizePaymentStatus(payload.paymentStatus);
    if (p) body.paymentStatus = p;

    const f = normalizeFulfillmentStatus(payload.fulfillmentStatus);
    if (f) body.fulfillmentStatus = f;

    if (payload.refund) body.refund = payload.refund;

    // 🔧 FIX: plural "statuses" to match BE
    const r = await http.patch<OrderDetail>(
      `/orders/${encodeURIComponent(id)}/statuses`,
      body
    );
    return r.data;
  },

  async updateShipping(
    id: string,
    payload: {
      methodCode?: string;
      methodLabel?: string;
      carrier?: string;
      trackingNumber?: string;
      trackingNumbers?: string[];
      estimatedDelivery?: string | null; // ISO
      shippedAt?: string | null; // ISO
      deliveredAt?: string | null; // ISO
    }
  ) {
    const r = await http.patch<OrderDetail>(
      `/orders/${encodeURIComponent(id)}/shipping`,
      payload
    );
    return r.data;
  },

  async syncShippingTracking(id: string) {
    const r = await http.post<{
      order: OrderDetail;
      tracking: {
        attempted: number;
        synced: number;
        errors?: string[];
        delivered?: boolean;
      };
    }>(`/orders/${encodeURIComponent(id)}/shipping/sync`);
    return r.data;
  },

  async updateMeta(id: string, payload: { notes?: string; tags?: string[] }) {
    const r = await http.patch<OrderDetail>(
      `/orders/${encodeURIComponent(id)}/meta`,
      payload
    );
    return r.data;
  },
  async sendEmail(
    id: string,
    template: "placed" | "paid" | "delivered" | "status_update"
  ) {
    const r = await http.post<{
      ok: boolean;
      alreadySent?: boolean;
      order?: OrderDetail;
    }>(
      `/orders/${encodeURIComponent(id)}/email`,
      { template }
    );
    return r.data;
  },
  async sendCustomEmail(
    id: string,
    payload: {
      subject: string;
      message: string;
      includeOrderSummary?: boolean;
    }
  ) {
    return http
      .post<{ ok: boolean }>(
        `/orders/${encodeURIComponent(id)}/email/custom`,
        payload
      )
      .then((r) => r.data);
  },
  async purgeCompletedOrderImages() {
    const r = await http.delete<OrderImageMaintenanceResponse>(
      "/orders/admin/completed-images"
    );
    return r.data;
  },
  async purgeOrderImages(id: string) {
    const r = await http.delete<OrderImageMaintenanceResponse>(
      `/orders/admin/${encodeURIComponent(id)}/images`
    );
    return r.data;
  },
  async deleteAllOrdersAndImages() {
    const r = await http.delete<OrderImageMaintenanceResponse>("/orders/admin/all");
    return r.data;
  },
};
